/**
 * Login Page — Nordic Sanctuary Design
 *
 * A calm, professional login experience following the JourFx design system.
 * Features subtle organic background shapes and sage-tinted aesthetics.
 *
 * SECURITY: Sign-up is disabled. Users must be pre-created via:
 * 1. Admin invitation workflow
 * 2. Database seed for initial users
 */
import { createFileRoute, useSearch, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { signIn } from "@/lib/auth";
import {
  Button,
  Input,
  Label,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@jourfx/ui";
import { Loader2, Eye, EyeOff, AlertCircle, Check } from "lucide-react";
import { useBrandLogoUrl } from "@/branding/BrandingProvider";

export const Route = createFileRoute("/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: (search.redirect as string) || "/dashboard",
  }),
  component: LoginPage,
});

function LoginPage() {
  const { t } = useTranslation("login");
  const { redirect } = useSearch({ from: "/login" });
  const primaryLogoUrl = useBrandLogoUrl("primary");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const [lockoutRemaining, setLockoutRemaining] = useState<number>(0);

  // Force light mode on the login page regardless of user/system theme
  useEffect(() => {
    const root = document.documentElement;
    const wasDark = root.classList.contains("dark");
    root.classList.remove("dark");
    root.classList.add("light");
    return () => {
      if (wasDark) {
        root.classList.remove("light");
        root.classList.add("dark");
      }
    };
  }, []);

  useEffect(() => {
    if (!lockedUntil) return;
    const update = () => {
      const remaining = Math.max(0, lockedUntil - Date.now());
      setLockoutRemaining(remaining);
      if (remaining <= 0) {
        setLockedUntil(null);
      }
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [lockedUntil]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLockedUntil(null);
    setIsLoading(true);

    try {
      const trimmedEmail = email.trim();
      if (!trimmedEmail) {
        setError(t("errors.emailRequired"));
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
        setError(t("errors.emailInvalid"));
        return;
      }
      if (!password) {
        setError(t("errors.passwordRequired"));
        return;
      }

      const result = await signIn.email({
        email: trimmedEmail,
        password,
      });
      if (result.error) {
        const err: any = result.error;
        const code = err?.code || err?.message || "";
        const retryAfter = err?.retryAfter || err?.data?.retryAfter;
        if (String(code).toLowerCase().includes("lock")) {
          if (retryAfter) {
            setLockedUntil(Date.now() + Number(retryAfter) * 1000);
          }
          throw new Error(t("errors.accountLocked"));
        }
        throw new Error(err?.message || t("errors.invalidCredentials"));
      }

      // Redirect to dashboard - useAuth hook will handle app user provisioning
      window.location.href = redirect || "/dashboard";
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("errors.signInFailed")
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden px-4 py-12 bg-[hsl(145_15%_95%)]">
      {/* Light gradient background */}
      <div className="pointer-events-none absolute inset-0">
        {/* Main gradient - sage to transparent */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              linear-gradient(135deg,
                hsl(145 15% 95%) 0%,
                hsl(60 9% 98%) 50%,
                hsl(145 10% 96%) 100%
              )
            `,
          }}
        />
        {/* Subtle mesh gradient overlay for depth */}
        <div
          className="absolute inset-0 opacity-60"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 20% 40%, hsl(145 20% 90% / 0.5) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 80% 60%, hsl(173 30% 92% / 0.4) 0%, transparent 50%)
            `,
          }}
        />
        {/* Top decorative wave */}
        <svg
          className="absolute -top-1 left-0 right-0 w-full text-white/40"
          viewBox="0 0 1440 120"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          style={{ height: "120px" }}
        >
          <path
            d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V0H1380C1320 0 1200 0 1080 0C960 0 840 0 720 0C600 0 480 0 360 0C240 0 120 0 60 0H0V120Z"
            fill="currentColor"
          />
        </svg>
        {/* Bottom decorative accent */}
        <div
          className="absolute bottom-0 left-0 right-0 h-32"
          style={{
            background:
              "linear-gradient(to top, hsl(145 10% 94% / 0.5), transparent)",
          }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 w-full max-w-md">
        {/* Logo above card with subtle animation */}
        <div className="mb-8 flex justify-center">
          <img
            src={primaryLogoUrl}
            alt="JourFx"
            className="h-12 w-auto drop-shadow-md transition-transform duration-300 hover:scale-105"
          />
        </div>

        {/* Login card - clean, elevated design per Nordic Sanctuary */}
        <Card className="border border-border/50 bg-card shadow-xl">
          <CardHeader className="space-y-1.5 pb-6 pt-6 text-center">
            <CardTitle className="text-2xl font-semibold tracking-tight text-foreground">
              {t("welcomeBack")}
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {t("signInSubtitle")}
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 px-6">
              {/* Error message */}
              {error && (
                <div
                  className="flex items-start gap-3 rounded-lg border border-destructive/20 bg-destructive/5 p-3.5 text-sm text-destructive"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {lockedUntil && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3.5 text-sm text-amber-900">
                  <p className="font-medium">{t("errors.lockedTitle")}</p>
                  <p className="mt-1">
                    {t("errors.lockedUntil", {
                      minutes: Math.ceil(lockoutRemaining / 60000) || 1,
                    })}
                  </p>
                  <Link to="/reset-password" className="mt-2 inline-block text-sm underline">
                    {t("errors.resetPassword")}
                  </Link>
                </div>
              )}

              {/* Email field */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  {t("form.email")}
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("form.emailPlaceholder")}
                  required
                  autoComplete="email"
                  className="h-11 bg-background transition-shadow focus-visible:shadow-md"
                />
              </div>

              {/* Password field with show/hide toggle */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  {t("form.password")}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t("form.passwordPlaceholder")}
                    required
                    autoComplete="current-password"
                    minLength={8}
                    className="h-11 bg-background pr-10 transition-shadow focus-visible:shadow-md"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={
                      showPassword ? t("form.hidePassword") : t("form.showPassword")
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember me & forgot password - improved mobile layout */}
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <label className="flex cursor-pointer items-center gap-2.5 select-none">
                  {/* Custom checkbox */}
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="peer sr-only"
                      id="remember-me"
                    />
                    <div className="h-5 w-5 rounded-md border-2 border-input bg-background transition-all peer-checked:border-primary peer-checked:bg-primary peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2" />
                    <Check className="absolute left-0.5 top-0.5 h-4 w-4 text-primary-foreground opacity-0 transition-opacity peer-checked:opacity-100" />
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {t("form.rememberMe")}
                  </span>
                </label>
                <Link
                  to="/reset-password"
                  className="text-sm font-medium text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  {t("form.forgotPassword")}
                </Link>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4 px-6 pb-6 pt-2">
              {/* Submit button with hover effect */}
              <Button
                type="submit"
                className="h-12 w-full text-base font-medium shadow-md transition-all hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t("buttons.signingIn")}
                  </>
                ) : (
                  t("buttons.signIn")
                )}
              </Button>

            </CardFooter>
          </form>
        </Card>

        {/* Footer links */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          <a
            href="/impressum"
            className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t("footer.imprint")}
          </a>
          <span className="hidden text-border sm:inline">|</span>
          <a
            href="/datenschutz"
            className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t("footer.privacy")}
          </a>
          <span className="hidden text-border sm:inline">|</span>
          <a
            href="/hilfe"
            className="transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            {t("footer.help")}
          </a>
        </div>
      </div>
    </div>
  );
}
